import React, { useEffect, useState, useRef } from 'react';
import { FilterState, LoanOfficer, LeadSource } from '../../types/throughput';
import { Button } from '../ui/Button';
import { RotateCcw, X, ChevronDown, Check, LayoutGrid, List, Calendar } from 'lucide-react';
const LEAD_SOURCES: LeadSource[] = ['Web', 'Referral', 'Partner', 'Phone', 'Email', 'Other'];
interface FilterBarProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  officers: LoanOfficer[];
  onRefresh: () => void;
}
export function FilterBar({
  filters,
  setFilters,
  officers,
  onRefresh
}: FilterBarProps) {
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setSourceDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleReset = () => {
    setFilters({
      ...filters,
      scopeType: 'All',
      selectedScopeId: undefined,
      includeInactive: false,
      leadSources: [],
      groupBySource: false
    });
  };
  const toggleLeadSource = (source: LeadSource) => {
    const current = filters.leadSources;
    const updated = current.includes(source) ? current.filter(s => s !== source) : [...current, source];
    setFilters({
      ...filters,
      leadSources: updated
    });
  };
  const svps = Array.from(new Set(officers.filter(o => o.role === 'SVP').map(o => JSON.stringify({
    id: o.id,
    name: o.name
  })))).map(s => JSON.parse(s));
  const teamLeads = Array.from(new Set(officers.filter(o => o.role === 'Team Lead').map(o => JSON.stringify({
    id: o.id,
    name: o.name
  })))).map(s => JSON.parse(s));
  const loanOfficers = officers.filter(o => o.role === 'Loan Officer');
  const inputClass = 'h-8 px-3 text-sm bg-white border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0176D3]/20 focus:border-[#0176D3] transition-colors';
  const labelClass = 'block text-xs font-medium text-[#706E6B] mb-1.5';
  return <div className="sticky top-0 z-30 w-full bg-white border-b border-[#E5E5E5]" data-id="element-49">
      <div className="max-w-[1600px] mx-auto px-6 py-4" data-id="element-50">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-4" data-id="element-51">
          {/* Date Range Group */}
          <div className="flex items-end gap-2" data-id="element-52">
            <div data-id="element-53">
              <label className={labelClass} data-id="element-54">
                <Calendar className="inline h-3 w-3 mr-1 -mt-0.5" data-id="element-55" />
                Cohort Start
              </label>
              <input type="date" value={filters.dateRange.start} onChange={e => setFilters({
              ...filters,
              dateRange: {
                ...filters.dateRange,
                start: e.target.value
              }
            })} className={`${inputClass} w-[140px]`} data-id="element-56" />
            </div>
            <span className="text-[#CCCCCC] pb-2" data-id="element-57">–</span>
            <div data-id="element-58">
              <label className={labelClass} data-id="element-59">End</label>
              <input type="date" value={filters.dateRange.end} onChange={e => setFilters({
              ...filters,
              dateRange: {
                ...filters.dateRange,
                end: e.target.value
              }
            })} className={`${inputClass} w-[140px]`} data-id="element-60" />
            </div>
          </div>

          {/* Divider */}
          <div className="hidden xl:block w-px h-8 bg-[#E5E5E5]" data-id="element-61" />

          {/* Scope Group */}
          <div className="flex items-end gap-2" data-id="element-62">
            <div data-id="element-63">
              <label className={labelClass} data-id="element-64">Scope</label>
              <select value={filters.scopeType} onChange={e => setFilters({
              ...filters,
              scopeType: e.target.value as any,
              selectedScopeId: undefined
            })} className={`${inputClass} w-[150px]`} data-id="element-65">
                <option value="All" data-id="element-66">All Loan Officers</option>
                <option value="SVP" data-id="element-67">By SVP</option>
                <option value="Team Lead" data-id="element-68">By Team Lead</option>
                <option value="Loan Officer" data-id="element-69">Single Officer</option>
              </select>
            </div>

            {filters.scopeType === 'SVP' && <div data-id="element-70">
                <label className={labelClass} data-id="element-71">Select SVP</label>
                <select value={filters.selectedScopeId || ''} onChange={e => setFilters({
              ...filters,
              selectedScopeId: e.target.value
            })} className={`${inputClass} w-[160px]`} data-id="element-72">
                  <option value="" data-id="element-73">Choose...</option>
                  {svps.map((svp: any) => <option key={svp.id} value={svp.id} data-id="element-74">
                      {svp.name}
                    </option>)}
                </select>
              </div>}

            {filters.scopeType === 'Team Lead' && <div data-id="element-75">
                <label className={labelClass} data-id="element-76">Select Team Lead</label>
                <select value={filters.selectedScopeId || ''} onChange={e => setFilters({
              ...filters,
              selectedScopeId: e.target.value
            })} className={`${inputClass} w-[160px]`} data-id="element-77">
                  <option value="" data-id="element-78">Choose...</option>
                  {teamLeads.map((tl: any) => <option key={tl.id} value={tl.id} data-id="element-79">
                      {tl.name}
                    </option>)}
                </select>
              </div>}

            {filters.scopeType === 'Loan Officer' && <div data-id="element-80">
                <label className={labelClass} data-id="element-81">Select Officer</label>
                <select value={filters.selectedScopeId || ''} onChange={e => setFilters({
              ...filters,
              selectedScopeId: e.target.value
            })} className={`${inputClass} w-[160px]`} data-id="element-82">
                  <option value="" data-id="element-83">Choose...</option>
                  {loanOfficers.map(lo => <option key={lo.id} value={lo.id} data-id="element-84">
                      {lo.name}
                    </option>)}
                </select>
              </div>}
          </div>

          {/* Divider */}
          <div className="hidden xl:block w-px h-8 bg-[#E5E5E5]" data-id="element-85" />

          {/* Lead Source */}
          <div className="relative" ref={dropdownRef} data-id="element-86">
            <label className={labelClass} data-id="element-87">Lead Source</label>
            <button type="button" onClick={() => setSourceDropdownOpen(!sourceDropdownOpen)} className={`${inputClass} flex items-center justify-between w-[150px] text-left`} data-id="element-88">
              <span className="truncate" data-id="element-89">
                {filters.leadSources.length === 0 ? 'All Sources' : filters.leadSources.length === 1 ? filters.leadSources[0] : `${filters.leadSources.length} selected`}
              </span>
              <ChevronDown className={`h-4 w-4 text-[#706E6B] transition-transform ${sourceDropdownOpen ? 'rotate-180' : ''}`} data-id="element-90" />
            </button>

            {sourceDropdownOpen && <div className="absolute top-full left-0 mt-1 w-[180px] bg-white border border-[#E5E5E5] rounded-lg shadow-lg z-50 py-1" data-id="element-91">
                {LEAD_SOURCES.map(source => <button key={source} type="button" onClick={() => toggleLeadSource(source)} className="flex items-center w-full px-3 py-2 text-sm text-[#181818] hover:bg-[#F5F5F5] transition-colors" data-id="element-92">
                    <div className={`w-4 h-4 mr-2.5 rounded border-2 flex items-center justify-center transition-colors ${filters.leadSources.includes(source) ? 'bg-[#0176D3] border-[#0176D3]' : 'border-[#CCCCCC]'}`} data-id="element-93">
                      {filters.leadSources.includes(source) && <Check className="h-3 w-3 text-white" strokeWidth={3} data-id="element-94" />}
                    </div>
                    {source}
                  </button>)}
                {filters.leadSources.length > 0 && <div className="border-t border-[#E5E5E5] mt-1 pt-1 px-3 pb-1" data-id="element-95">
                    <button type="button" onClick={() => setFilters({
                ...filters,
                leadSources: []
              })} className="text-xs text-[#0176D3] hover:underline" data-id="element-96">
                      Clear all
                    </button>
                  </div>}
              </div>}
          </div>

          {/* View Toggle */}
          <div data-id="element-97">
            <label className={labelClass} data-id="element-98">View</label>
            <div className="flex h-8 bg-[#F5F5F5] rounded-lg p-0.5" data-id="element-99">
              <button type="button" onClick={() => setFilters({
              ...filters,
              groupBySource: false
            })} className={`flex items-center gap-1.5 px-3 text-xs font-medium rounded-md transition-all ${!filters.groupBySource ? 'bg-white text-[#181818] shadow-sm' : 'text-[#706E6B] hover:text-[#181818]'}`} data-id="element-100">
                <List className="h-3.5 w-3.5" data-id="element-101" />
                Overall
              </button>
              <button type="button" onClick={() => setFilters({
              ...filters,
              groupBySource: true
            })} className={`flex items-center gap-1.5 px-3 text-xs font-medium rounded-md transition-all ${filters.groupBySource ? 'bg-white text-[#181818] shadow-sm' : 'text-[#706E6B] hover:text-[#181818]'}`} data-id="element-102">
                <LayoutGrid className="h-3.5 w-3.5" data-id="element-103" />
                By Source
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden xl:block w-px h-8 bg-[#E5E5E5]" data-id="element-104" />

          {/* Options & Actions */}
          <div className="flex items-end gap-3" data-id="element-105">
            <label className="flex items-center gap-2 h-8 text-sm text-[#3E3E3C] cursor-pointer select-none" data-id="element-106">
              <input type="checkbox" checked={filters.includeInactive} onChange={e => setFilters({
              ...filters,
              includeInactive: e.target.checked
            })} className="w-4 h-4 rounded border-[#CCCCCC] text-[#0176D3] focus:ring-[#0176D3] focus:ring-offset-0" data-id="element-107" />
              <span className="text-xs" data-id="element-108">Include Inactive</span>
            </label>

            <Button variant="neutral" size="sm" onClick={handleReset} data-id="element-109">
              <X className="h-3.5 w-3.5 mr-1.5" data-id="element-110" />
              Clear
            </Button>
            <Button variant="primary" size="sm" onClick={onRefresh} data-id="element-111">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" data-id="element-112" />
              Refresh
            </Button>
          </div>
        </div>
      </div>
    </div>;
}